import { useEffect } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import BulletListIcon from "@atlaskit/icon/glyph/bullet-list";
import CalendarIcon from "@atlaskit/icon/glyph/calendar";
import FeedbackIcon from "@atlaskit/icon/glyph/feedback";
import FileIcon from "@atlaskit/icon/glyph/file";
import FlagFilledIcon from "@atlaskit/icon/glyph/flag-filled";
import GraphLineIcon from "@atlaskit/icon/glyph/graph-line";
import IssuesIcon from "@atlaskit/icon/glyph/issues";
import MenuIcon from "@atlaskit/icon/glyph/menu";
import QuestionCircleIcon from "@atlaskit/icon/glyph/question-circle";
import RoadmapIcon from "@atlaskit/icon/glyph/roadmap";
import SendIcon from "@atlaskit/icon/glyph/send";
import StarFilledIcon from "@atlaskit/icon/glyph/star-filled";
import { media } from "@atlaskit/primitives";
import FeatureGates from "@atlassian/feature-gate-js-client";

import { useLocalizedHref } from "@/libraries/web-framework";

import {
    OrangeModal,
    PurpleModal,
    GreenModal,
    BlueModal,
    BlackModal
} from "./components/assets";
import {
    getProductDesignModalImage,
    getProductItModalImage,
    getProductMarketingModalImage,
    getProductOperationsModalImage,
    getProductSoftwareDevelopmentModalImage
} from "./components/assets/localized-images";
import { TemplateKey } from "./constants/constants";
import { useHeroState } from "./util/hero-context";

import {
    ProductModalCard,
    ProductModalCarousel,
    ProductModalCardType
} from ".";

const modalBackgroundStyles = css({
    position: "fixed",
    insetInlineStart: "0",
    insetBlockStart: "0",
    insetInlineEnd: "0",
    insetBlockEnd: "0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
        "var(--DarkNeutral-Opaque-DarkNeutral-100, rgba(16, 18, 20, 0.85))",
    zIndex: "10",
    [media.above.lg]: {
        display: "none"
    }
});

export const JswProductModalCarousel = () => {
    const { modalIndex } = useHeroState();
    const intl = useIntl();

    // ATLAS-61644: Determine experiment enrollment for setting CTA link.
    const setPersonaData = FeatureGates.getExperimentValue(
        "jfind-anchored-journeys-in-signup-exp",
        "setPersonaData",
        false,
        {
            fireExperimentExposure: false
        }
    );

    const showUpdatedHeroExperiment: boolean =
        FeatureGates.getExperimentValue<boolean>(
            "organic_jira_product_tour_overview_hero_v2",
            "showExperiment",
            false
        );

    // Disable scroll if modal is open
    useEffect(() => {
        if (modalIndex !== -1) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
    }, [modalIndex]);

    const softwareModal: ProductModalCardType = {
        eyebrow: intl.formatMessage({
            id: "jira-product-tour.hero-modal-software-development.eyebrow-text",
            defaultMessage: "product and issue tracking"
        }),
        title: intl.formatMessage({
            id: "jira-product-tour.hero-modal-software-development.title-text",
            defaultMessage: "Software Development"
        }),
        description: intl.formatMessage({
            id: "jira-product-tour.hero-modal-software-development.description-text",
            defaultMessage:
                "Plan, track, release and support world-class software. Jira is the single source of truth for your entire development lifecycle."
        }),
        image: getProductSoftwareDevelopmentModalImage(intl.locale),
        iconCollection: [
            {
                icon: <GraphLineIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-software-development-icon-analytics.icon-text",
                    defaultMessage: "Analytics"
                })
            },
            {
                icon: <RoadmapIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-software-development-icon-progress-tracking.icon-text",
                    defaultMessage: "Progress tracking"
                })
            },
            {
                icon: <IssuesIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-software-development-icon-sprint-planning.icon-text",
                    defaultMessage: "Sprint planning"
                })
            },
            {
                icon: <MenuIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-software-development-icon-forms.icon-text",
                    defaultMessage: "Forms"
                })
            }
        ],
        BackgroundImage: OrangeModal,
        ctaLink: useLocalizedHref(
            "/software/jira/free?jiraProductTourSignupType=software-development"
        ),
        label: "software-development",
        personaData: {
            team: "Software Development",
            persona: "Software Development Generic"
        }
    };

    const marketingModal: ProductModalCardType = {
        eyebrow: intl.formatMessage({
            id: "jira-product-tour.hero-modal-marketing.eyebrow-text",
            defaultMessage: "plan and launch campaigns"
        }),
        title: intl.formatMessage({
            id: "jira-product-tour.hero-modal-marketing.title-text",
            defaultMessage: "Marketing"
        }),
        description: intl.formatMessage({
            id: "jira-product-tour.hero-modal-marketing.description-text",
            defaultMessage:
                "Make launching viral marketing campaigns a breeze. Break down complex campaigns into actionable steps, easily collect requests, and stay aligned with cross-functional teams."
        }),
        image: getProductMarketingModalImage(intl.locale),
        iconCollection: [
            {
                icon: <BulletListIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-marketing-icon-progress-tracking.icon-text",
                    defaultMessage: "Progress tracking"
                })
            },
            {
                icon: <CalendarIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-marketing-icon-calendar-view.icon-text",
                    defaultMessage: "Calendar view"
                })
            },
            {
                icon: <MenuIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-marketing-icon-forms.icon-text",
                    defaultMessage: "Forms"
                })
            },
            {
                icon: <FileIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-marketing-icon-reporting.icon-text",
                    defaultMessage: "Reporting"
                })
            }
        ],
        BackgroundImage: PurpleModal,
        ctaLink: useLocalizedHref(
            `/software/jira/free?templateId=${
                setPersonaData
                    ? TemplateKey.CampaignManagement
                    : TemplateKey.GoToMarket
            }`
        ),
        label: "marketing",
        personaData: {
            team: "Marketing",
            persona: "Marketing Generic"
        }
    };

    const itModal: ProductModalCardType = {
        eyebrow: intl.formatMessage({
            id: "jira-product-tour.hero-modal-it.eyebrow-text",
            defaultMessage: "manage and track requests"
        }),
        title: intl.formatMessage({
            id: "jira-product-tour.hero-modal-it.title-text",
            defaultMessage: "IT support services"
        }),
        description: intl.formatMessage({
            id: "jira-product-tour.hero-modal-it.description-text",
            defaultMessage:
                "Easily build custom forms, track requests, and fully automate your intake process. Communicate progress and collaborate directly on requests with internal stakeholders in one place."
        }),
        image: getProductItModalImage(intl.locale),
        iconCollection: [
            {
                icon: <FlagFilledIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-it-icon-resolve-incidents.icon-text",
                    defaultMessage: "Resolve incidents"
                })
            },
            {
                icon: <QuestionCircleIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-it-icon-help-desk.icon-text",
                    defaultMessage: "Help desk"
                })
            },
            {
                icon: <SendIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-it-icon-manage-requests.icon-text",
                    defaultMessage: "Manage requests"
                })
            },
            {
                icon: <FeedbackIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-it-icon-reporting.icon-text",
                    defaultMessage: "Reporting"
                })
            }
        ],
        BackgroundImage: GreenModal,
        ctaLink: useLocalizedHref(
            "/software/jira/free?jiraProductTourSignupType=it"
        ),
        label: "it",
        personaData: {
            team: "IT Support",
            persona: "IT Support Generic"
        }
    };

    const designModal: ProductModalCardType = {
        eyebrow: intl.formatMessage({
            id: "jira-product-tour.hero-modal-design.eyebrow-text",
            defaultMessage: "build creative workflows"
        }),
        title: intl.formatMessage({
            id: "jira-product-tour.hero-modal-design.title-text",
            defaultMessage: "Design"
        }),
        description: intl.formatMessage({
            id: "jira-product-tour.hero-modal-design.description-text",
            defaultMessage:
                "Collaborate on, organize, and deliver creative requests with ease. Give real-time visibility into designs with Jira’s Figma integration and accelerate approvals."
        }),
        image: getProductDesignModalImage(intl.locale),
        iconCollection: [
            {
                icon: <BulletListIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-design-task-planning.icon-text",
                    defaultMessage: "Task planning"
                })
            },
            {
                icon: <QuestionCircleIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-design-progress-tracking.icon-text",
                    defaultMessage: "Progress tracking"
                })
            },
            {
                icon: <SendIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-design-prioritization.icon-text",
                    defaultMessage: "Prioritization"
                })
            },
            {
                icon: <FileIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-design-forms.icon-text",
                    defaultMessage: "Forms"
                })
            }
        ],
        BackgroundImage: BlueModal,
        ctaLink: useLocalizedHref(
            `/software/jira/free?templateId=${TemplateKey.WebDesignProcess}`
        ),
        label: "design",
        personaData: {
            team: "Design",
            persona: "Design Generic"
        }
    };

    const operationsModal: ProductModalCardType = {
        eyebrow: intl.formatMessage({
            id: "jira-product-tour.hero-modal-operations.eyebrow-text",
            defaultMessage: "create intake processes"
        }),
        title: intl.formatMessage({
            id: "jira-product-tour.hero-modal-operations.title-text",
            defaultMessage: "Operations"
        }),
        description: intl.formatMessage({
            id: "jira-product-tour.hero-modal-operations.description-text",
            defaultMessage:
                "Manage procurement, office, and new vendor requests, craft new operational processes, monitor operational risks and more. Keep your company running at maximum efficiency with Jira."
        }),
        image: getProductOperationsModalImage(intl.locale),
        iconCollection: [
            {
                icon: <BulletListIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-operations-task-planning.icon-text",
                    defaultMessage: "Task planning"
                })
            },
            {
                icon: <RoadmapIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-operations-progress-tracking.icon-text",
                    defaultMessage: "Progress tracking"
                })
            },
            {
                icon: <StarFilledIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-operations-prioritization.icon-text",
                    defaultMessage: "Prioritization"
                })
            },
            {
                icon: <MenuIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-operations-forms.icon-text",
                    defaultMessage: "Forms"
                })
            }
        ],
        BackgroundImage: BlackModal,
        ctaLink: useLocalizedHref(
            `/software/jira/free?templateId=${TemplateKey.SimplifiedProcessControl}`
        ),
        label: "operations",
        personaData: {
            team: "Operations",
            persona: "Operations Generic"
        }
    };

    const JswProductTourModalCards: Array<ProductModalCardType> = [
        softwareModal,
        marketingModal,
        itModal,
        designModal,
        operationsModal
    ];

    const experimentJswProductTourModalCards: Array<ProductModalCardType> = [
        {
            ...softwareModal,
            eyebrow: "software development",
            title: "Project & issue tracking"
        },
        {
            ...marketingModal,
            eyebrow: "marketing",
            title: "Plan & launch campaigns"
        },
        {
            ...itModal,
            eyebrow: "it services",
            title: "Manage & track requests",
            iconCollection: [
                {
                    icon: <FlagFilledIcon size="small" label="" />,
                    description: intl.formatMessage({
                        id: "jira-product-tour.hero-modal-it-icon-resolve-incidents.icon-text",
                        defaultMessage: "Resolve incidents"
                    })
                },
                {
                    icon: <SendIcon size="small" label="" />,
                    description: intl.formatMessage({
                        id: "jira-product-tour.hero-modal-it-icon-manage-requests.icon-text",
                        defaultMessage: "Manage requests"
                    })
                },
                {
                    icon: <FeedbackIcon size="small" label="" />,
                    description: intl.formatMessage({
                        id: "jira-product-tour.hero-modal-it-icon-reporting.icon-text",
                        defaultMessage: "Reporting"
                    })
                }
            ]
        },
        {
            ...designModal,
            eyebrow: "design",
            title: "Build creative workflows"
        },
        {
            ...operationsModal,
            eyebrow: "operations",
            title: "Create intake processes"
        }
    ];

    const renderedArray = showUpdatedHeroExperiment
        ? experimentJswProductTourModalCards
        : JswProductTourModalCards;

    return (
        <div>
            {modalIndex !== -1 && (
                <>
                    {/* desktop */}
                    <ProductModalCarousel>
                        {renderedArray.map((props, idx) => {
                            return <ProductModalCard key={idx} {...props} />;
                        })}
                    </ProductModalCarousel>
                    {/* Tablet / Mobile */}
                    <div css={modalBackgroundStyles}>
                        <ProductModalCard
                            {...(renderedArray[
                                modalIndex
                            ] as ProductModalCardType)}
                        />
                    </div>
                </>
            )}
        </div>
    );
};
